import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { SectionContainer } from "../../../pages/shared.styled";
import { useEditorNavigation } from "../stores/EditorNavigationContext";

const linkClass = (isActive) =>
  [
    "block px-4 py-4 text-sm font-medium whitespace-nowrap no-underline",
    isActive
      ? "bg-white text-primary-dark font-semibold"
      : "text-gray-darker hover:bg-white/60 hover:text-primary-dark",
  ].join(" ");

function NavItem({ to, end, children }) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) => linkClass(isActive)}
      >
        {children}
      </NavLink>
    </li>
  );
}

NavItem.propTypes = {
  to: PropTypes.string.isRequired,
  end: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

NavItem.defaultProps = {
  end: false,
};

/** Nav item that runs editor before-navigate (e.g. submit new product) then navigates. */
function EditorNavItem({ to, end, children }) {
  const { navigateTo } = useEditorNavigation();
  const location = useLocation();

  const href = to;
  const pathname = location.pathname.replace(/\/$/, "") || "/";
  const toNormalized = (typeof href === "string" ? href : "").replace(/\/$/, "") || "/";
  const isActive = end
    ? pathname === toNormalized || pathname === `${toNormalized}/`
    : pathname === toNormalized || pathname.startsWith(`${toNormalized}/`);

  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      navigateTo(href);
    },
    [href, navigateTo]
  );

  return (
    <li>
      <a
        href={href}
        className={linkClass(isActive)}
        onClick={handleClick}
        aria-current={isActive ? "page" : undefined}
      >
        {children}
      </a>
    </li>
  );
}

EditorNavItem.propTypes = {
  to: PropTypes.string.isRequired,
  end: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

EditorNavItem.defaultProps = {
  end: false,
};

/**
 * A nav group button that opens a flyout list of chapter links.
 * Keyboard: Space/Enter toggles, Escape closes and returns focus to button,
 * Tab naturally moves through items and closes when focus leaves the group.
 */
function NavGroup({ label, chapters, buildTo }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const buttonRef = useRef(null);
  const id = `nav-group-${label.replace(/\s+/g, "-").toLowerCase()}`;
  const editorNav = useEditorNavigation();

  // Close on outside click, Escape, or focus leaving the group
  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    // Close when focus moves outside the group (e.g. Tab past last item)
    const handleFocusOut = (e) => {
      if (ref.current && !ref.current.contains(e.relatedTarget)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    ref.current?.addEventListener("focusout", handleFocusOut);
    const groupEl = ref.current;
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
      groupEl?.removeEventListener("focusout", handleFocusOut);
    };
  }, [open]);

  if (!chapters.length) return null;

  return (
    <li ref={ref} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((prev) => !prev)}
        className={[
          "flex items-center gap-1 px-4 py-4 text-sm font-medium whitespace-nowrap",
          "appearance-none bg-transparent border-0 cursor-pointer",
          "text-gray-darker hover:bg-white/60 hover:text-primary-dark",
        ].join(" ")}
      >
        {label}
        <span aria-hidden="true" className="text-xs opacity-60">
          {open ? "▲" : "▼"}
        </span>
      </button>
      {open && (
        <ul
          id={id}
          className="absolute top-full left-0 z-20 bg-white shadow-lg border border-gray-lighter rounded m-0 p-0 list-none min-w-max py-1"
        >
          {chapters.map((chapter) => {
            const chapterTo = buildTo(`/chapter/${chapter.id}`);
            const handleChapterClick = editorNav
              ? (e) => { e.preventDefault(); editorNav.navigateTo(chapterTo); setOpen(false); }
              : () => setOpen(false);
            return (
              <li key={chapter.id}>
                <NavLink
                  to={chapterTo}
                  onClick={handleChapterClick}
                  className={({ isActive }) =>
                    [
                      "block px-4 py-2 text-sm font-medium whitespace-nowrap",
                      isActive
                        ? "bg-primary-lighter text-primary-dark font-semibold"
                        : "text-gray-darker hover:bg-primary-lighter hover:text-primary-dark",
                    ].join(" ")
                  }
                >
                  {chapter.label ?? chapter.short_label}
                </NavLink>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

NavGroup.propTypes = {
  label: PropTypes.string.isRequired,
  chapters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string,
      short_label: PropTypes.string,
    })
  ).isRequired,
  buildTo: PropTypes.func.isRequired,
};

/**
 * Editor step navigation.
 * Sticky at the top of the viewport after scrolling past. Full-width background always;
 * nav items are constrained to SectionContainer width.
 * Chapter groups (WCAG, 508, EN 301 549, etc.) are rendered as accessible flyout buttons.
 */
function Navigation({ chapterGroups, basePath, isStuck, showDraftsTab }) {
  const editorNav = useEditorNavigation();
  const LinkComponent = editorNav ? EditorNavItem : NavItem;

  const to = (path) => {
    if (!basePath) return path || "";
    const base = basePath.replace(/\/$/, "");
    if (!path) return base;
    return path.startsWith("/") ? `${base}${path}` : `${base}/${path}`;
  };

  return (
    <nav
      className={`sticky top-0 z-10 ${!isStuck ? "bg-primary-lighter" : "bg-transparent"}`}
      aria-label="Editor navigation"
    >
      <SectionContainer className="!py-0 px-20">
        <ul className={`flex flex-row items-center m-0 p-0 list-none ${isStuck ? "bg-primary-lighter" : ""}`}>
          {showDraftsTab && <LinkComponent to={to("")} end>My Draft ACRs</LinkComponent>}
          <LinkComponent to={to("/overview")}>Overview</LinkComponent>
          <LinkComponent to={to("/about")}>About</LinkComponent>
          {chapterGroups.map((group) => (
            <NavGroup
              key={group.id}
              label={group.label}
              chapters={group.chapters}
              buildTo={to}
            />
          ))}
          <LinkComponent to={to("/report")}>Report</LinkComponent>
          <LinkComponent to={to("/glossary")}>Glossary</LinkComponent>
        </ul>
      </SectionContainer>
    </nav>
  );
}

Navigation.propTypes = {
  chapterGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      chapters: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          label: PropTypes.string,
          short_label: PropTypes.string,
        })
      ).isRequired,
    })
  ),
  basePath: PropTypes.string,
  isStuck: PropTypes.bool,
  showDraftsTab: PropTypes.bool,
};

Navigation.defaultProps = {
  chapterGroups: [],
  basePath: "",
  isStuck: false,
  showDraftsTab: false,
};

export default Navigation;
